
import {
  computed,
  defineComponent,
  PropType,
  Ref,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { SchemaItem } from '@/composables/custom-data/customData';
import SpmOverlayPanel from '@/components/spm-primevue/SpmOverlayPanel.vue';
import {
  resolveRelationPreview,
  CustomObjectRelationDefinition,
  RelationPreviewResult,
} from '@/composables/custom-data/relationResolver';

export default defineComponent({
  name: 'RelationPopoverCard',
  components: {
    SpmOverlayPanel,
  },
  props: {
    relation: {
      type: Object as PropType<CustomObjectRelationDefinition>,
      required: true,
    },
    relationValue: {
      type: [String, Number] as PropType<string | number>,
      required: true,
    },
    customSchemas: {
      type: Array as PropType<SchemaItem[]>,
      default: () => [],
    },
  },
  setup(props) {
    const { t } = useI18n();
    const loading = ref(false);
    const preview = ref<RelationPreviewResult | null>(null);
    const errorMessage = ref('');
    const hasRecord = ref(true);
    const expandedFields: Ref<Record<string, boolean>> = ref({});
    const INLINE_TRUNCATE_LIMIT = 120;
    const MULTILINE_TRUNCATE_LIMIT = 180;

    const requestKey = computed(() => [
      props.relation.targetSchemaType,
      props.relation.targetSchema,
      props.relation.targetField,
      String(props.relationValue),
    ].join(':'));

    const fallbackTitle = computed(() => props.relation.targetSchema);
    const fallbackSubtitle = computed(() => (
      props.relation.targetSchemaType === 'system'
        ? t('customData.listing.relationPopover.subtitle.system')
        : t('customData.listing.relationPopover.subtitle.custom')
    ));

    const isMetaField = (fieldKey: string): boolean => (
      fieldKey === '__meta_created_at' || fieldKey === '__meta_updated_at'
    );

    const getTranslatedFieldLabel = (fieldKey: string, fallbackLabel: string): string => {
      if (fieldKey === '__meta_created_at') {
        return t('customData.listing.default_fields_name.created_at');
      }
      if (fieldKey === '__meta_updated_at') {
        return t('customData.listing.default_fields_name.updated_at');
      }
      if (fieldKey === 'created_at' || fieldKey === 'date_creation') {
        return t('customData.listing.default_fields_name.created_at');
      }
      if (fieldKey === 'updated_at' || fieldKey === 'date_modification') {
        return t('customData.listing.default_fields_name.updated_at');
      }
      if (fieldKey === 'contact_first_name') {
        return t('myLists.manage.customers.headers.firstname');
      }
      if (fieldKey === 'contact_last_name') {
        return t('myLists.manage.customers.headers.lastname');
      }
      if (fieldKey === 'contact_data_email') {
        return t('myLists.manage.customers.headers.email');
      }
      if (fieldKey === 'reference') {
        return t('myShopData.products.headers.reference');
      }
      if (fieldKey === 'name') {
        return t('myShopData.products.headers.name');
      }
      if (fieldKey === 'link') {
        return t('myShopData.products.headers.link');
      }
      if (fieldKey === 'price') {
        return t('myShopData.products.headers.price');
      }
      return fallbackLabel;
    };

    const getLinkDisplayText = (href: string): string => {
      try {
        const parsedUrl = new URL(href);
        return parsedUrl.hostname.replace(/^www\./, '');
      } catch (error) {
        return t('customData.listing.relationPopover.openLink');
      }
    };

    const displayFields = computed(() => {
      if (!preview.value) return [];

      return preview.value.fields
        .map((field, index) => ({
          ...field,
          uiKey: `${field.key}-${index}`,
          label: getTranslatedFieldLabel(field.key, field.label),
          isMeta: isMetaField(field.key),
        }))
        .sort((fieldA, fieldB) => Number(fieldA.isMeta) - Number(fieldB.isMeta));
    });

    const isFieldExpanded = (fieldUiKey: string): boolean => !!expandedFields.value[fieldUiKey];

    const toggleFieldExpansion = (fieldUiKey: string) => {
      expandedFields.value = {
        ...expandedFields.value,
        [fieldUiKey]: !expandedFields.value[fieldUiKey],
      };
    };

    const isMultilineField = (field: any): boolean => field.displayMode === 'multiline';

    const isFieldExpandable = (field: any): boolean => {
      if (field.fieldType === 'external_link') return false;
      if (!field.value) return false;
      const limit = isMultilineField(field) ? MULTILINE_TRUNCATE_LIMIT : INLINE_TRUNCATE_LIMIT;
      return String(field.value).length > limit;
    };

    const getFieldDisplayValue = (field: any): string => {
      if (!isFieldExpandable(field)) return field.value;
      if (isFieldExpanded(field.uiKey)) return field.value;
      if (isMultilineField(field)) return field.value;
      return `${String(field.value).slice(0, INLINE_TRUNCATE_LIMIT)}...`;
    };

    const getFieldValueClass = (field: any): string => {
      if (field.fieldType === 'external_link') return '';
      if (!isFieldExpandable(field)) return '';
      if (isFieldExpanded(field.uiKey)) return 'relation-popover__field-value--expanded';
      if (isMultilineField(field)) return 'relation-popover__field-value--multiline-collapsed';
      return 'relation-popover__field-value--inline-collapsed';
    };

    const loadPreview = async (forceRefresh = false) => {
      loading.value = true;
      errorMessage.value = '';
      try {
        const response = await resolveRelationPreview({
          relation: props.relation,
          relationValue: props.relationValue,
          customSchemas: props.customSchemas,
          forceRefresh,
        });
        preview.value = response;
        hasRecord.value = !!response;
      } catch (error) {
        preview.value = null;
        hasRecord.value = false;
        errorMessage.value = t('customData.listing.relationPopover.error');
      } finally {
        loading.value = false;
      }
    };

    const onTriggerClick = async () => {
      await loadPreview(true);
    };

    watch(requestKey, () => {
      preview.value = null;
      errorMessage.value = '';
      hasRecord.value = true;
      expandedFields.value = {};
    });

    return {
      t,
      loading,
      preview,
      errorMessage,
      hasRecord,
      displayFields,
      isFieldExpanded,
      isFieldExpandable,
      getFieldDisplayValue,
      getFieldValueClass,
      toggleFieldExpansion,
      fallbackTitle,
      fallbackSubtitle,
      getLinkDisplayText,
      onTriggerClick,
    };
  },
});
